import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  useSeries,
  useSeriesDetail,
  useBudgets,
  useBudgetDetail,
  useAllocationSources,
} from "./useForecastExplorer";
import { useAllocationSourceSeries } from "../useForecastLab";
import SeriesRankTable from "./SeriesRankTable";
import SeriesDetail from "./SeriesDetail";
import BudgetRankTable from "./BudgetRankTable";
import BudgetDetail from "./BudgetDetail";
import AllocationSourceRankTable from "./AllocationSourceRankTable";
import AllocationSourceDetail from "./AllocationSourceDetail";

const HORIZONS = [30, 60, 90];
// "Breakdown" (renamed from "Group") — "all" means no breakdown, so it reads "Total".
const GROUPS = [
  { value: "all", label: "Total" },
  { value: "resource", label: "Resource" },
  { value: "project", label: "Project" },
];
// The Explorer subviews — content destinations, rendered as flat navigation tabs.
const LENSES = [
  { value: "cost-series", label: "Cost Series" },
  { value: "budgets", label: "Budgets" },
  { value: "allocation", label: "Allocation sources" },
];

// Segmented — a compact, low-weight parameter control (32px, one soft accent on the
// active option). Reserved for stable parameter choices (horizon, breakdown), never
// for navigation.
function Segmented({ label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">{label}</span>
      <div className="inline-flex overflow-hidden rounded-ctrl border border-solid border-card-bd" style={{ height: 32 }}>
        {options.map((o, i) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className={`box-border border-0 px-2.5 text-[12.5px] transition-colors ${i !== 0 ? "border-l border-solid border-card-bd" : ""} ${
                active ? "bg-primary-soft font-semibold text-primary" : "bg-card font-medium text-text-3 hover:bg-surface-2 hover:text-text"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
Segmented.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  options: PropTypes.array,
};

export default function ForecastExplorer({ scope, initial = {} }) {
  // A deep-link (initial.*) wins; otherwise fall back to the scope-derived
  // default lens. The toggles still let the user move freely afterwards.
  const [lens, setLens] = useState(
    initial.lens || (scope === "allocation" ? "allocation" : "cost-series")
  );
  // Default to 30 days to match the other dashboards; a deep-link can override.
  const [horizon, setHorizon] = useState(initial.horizon || 30);
  const [group, setGroup] = useState("all");
  const [selected, setSelected] = useState(initial.selected != null ? initial.selected : null);
  const [sortKey, setSortKey] = useState("growth");

  // Each list query only runs for its active lens; detail queries additionally
  // gate on a selection (passing null disables them).
  const seriesQuery = useSeries(group, horizon, lens === "cost-series");
  const seriesDetailQuery = useSeriesDetail(lens === "cost-series" ? selected : null, horizon);
  const budgetsQuery = useBudgets(horizon, lens === "budgets");
  // The budgets lens lists only DEFINED budgets — the endpoint also returns
  // org-unit ("owner") rows (no cap) for context, which we filter out here.
  const definedBudgets = (budgetsQuery.data || []).filter((r) => r.kind !== "owner");
  const budgetDetailQuery = useBudgetDetail(lens === "budgets" ? selected : null, horizon);
  const allocSourcesQuery = useAllocationSources(horizon, lens === "allocation");
  const allocSelected = lens === "allocation" ? selected : null;
  const allocDetailQuery = useAllocationSourceSeries(
    allocSelected,
    horizon,
    !!allocSelected
  );

  // Reset selection when lens changes
  function handleLensChange(newLens) {
    setLens(newLens);
    setSelected(null);
  }

  // Drill from a budget contributor into its cost-series detail.
  function handleDrillSeries(seriesName) {
    setLens("cost-series");
    setSelected(seriesName);
  }

  // Derive the budget name and kind from the currently selected budget row.
  const selectedBudgetRow = definedBudgets.find((r) => r.id === selected);
  const selectedBudgetName = selectedBudgetRow ? selectedBudgetRow.name : undefined;
  const selectedBudgetKind = selectedBudgetRow ? selectedBudgetRow.kind : undefined;

  return (
    <div className="space-y-4">
      {/* Row 3: Explorer subviews as flat navigation tabs (smaller/quieter than the
          domain tabs above), with the analytical parameter controls on the right.
          An attached flat tertiary header row — full-bleed and flush under the domain
          tabs (negative margins cancel the page padding), lightly tinted, straight
          edges, a thin bottom border the active tab underline sits on. */}
      <div className="-mx-6 -mt-4 flex flex-wrap items-end gap-4 border-b border-solid border-card-bd bg-side px-6" style={{ minHeight: 50 }}>
        {/* Lens tabs — content destinations → flat/underline navigation styling. The
            active tab's 2px border sits on (overlaps) the bar's bottom border. */}
        <div className="flex items-end gap-1">
          {LENSES.map((l) => {
            const active = lens === l.value;
            return (
              <button
                key={l.value}
                type="button"
                onClick={() => handleLensChange(l.value)}
                aria-current={active ? "page" : undefined}
                className={`-mb-px box-border border-0 border-b-2 border-solid px-3 py-2.5 text-[13px] whitespace-nowrap transition-colors ${
                  active
                    ? "border-primary font-semibold text-text"
                    : "border-transparent font-medium text-text-3 hover:text-text-2"
                }`}
              >
                {l.label}
              </button>
            );
          })}
        </div>

        {/* Analytical parameter controls — compact, low-weight segmented. */}
        <div className="ml-auto flex flex-wrap items-center gap-3 self-center py-1.5">
          <Segmented
            label="Horizon:"
            value={horizon}
            onChange={setHorizon}
            options={HORIZONS.map((h) => ({ value: h, label: `${h}d` }))}
          />
          {lens === "cost-series" && (
            <Segmented label="Breakdown:" value={group} onChange={setGroup} options={GROUPS} />
          )}
        </div>
      </div>

      {/* Content */}
      {lens === "cost-series" && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <SeriesRankTable
              data={seriesQuery.data}
              isLoading={seriesQuery.isLoading}
              selected={selected}
              onSelect={setSelected}
              sortKey={sortKey}
              onSortKey={setSortKey}
            />
          </div>
          {selected && (
            <div className="flex-1 min-w-0">
              <SeriesDetail
                name={selected}
                horizon={horizon}
                data={seriesDetailQuery.data}
                isLoading={seriesDetailQuery.isLoading}
              />
            </div>
          )}
        </div>
      )}

      {lens === "budgets" && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <BudgetRankTable
              data={definedBudgets}
              isLoading={budgetsQuery.isLoading}
              selected={selected}
              onSelect={setSelected}
            />
          </div>
          {selected && (
            <div className="flex-1 min-w-0">
              <BudgetDetail
                budgetId={selected}
                budgetName={selectedBudgetName}
                budgetKind={selectedBudgetKind}
                horizon={horizon}
                onDrillSeries={handleDrillSeries}
                data={budgetDetailQuery.data}
                isLoading={budgetDetailQuery.isLoading}
              />
            </div>
          )}
        </div>
      )}

      {lens === "allocation" && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <AllocationSourceRankTable
              data={allocSourcesQuery.data?.sources}
              isLoading={allocSourcesQuery.isLoading}
              selected={selected}
              onSelect={setSelected}
            />
          </div>
          {selected && (
            <div className="flex-1 min-w-0">
              <AllocationSourceDetail
                sourceKey={selected}
                horizon={horizon}
                data={allocDetailQuery.data}
                isLoading={allocDetailQuery.isLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

ForecastExplorer.propTypes = {
  scope: PropTypes.string,
  // Deep-link seed: { lens, horizon, selected } from the dashboard.
  initial: PropTypes.shape({
    lens: PropTypes.string,
    horizon: PropTypes.number,
    selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};
